import { CREDITS_VISIBLE } from "../lib/credits";
import { useState } from "react";
import { HelpCircle } from "lucide-react";
import type { MatchCard } from "@hub/shared";
import { Button, Modal } from "@/ui";
import { possessive } from "../lib/cards";
import s from "../network.module.css";

/** Plain-language lines describing who sees what on this card. Exported for tests and for the detail view. */
export function perspectiveLines(card: MatchCard): { you: string; them: string } {
  const r = card.roles;
  const needer = card.needer;
  const helper = card.helper;
  const youOwnNeed = r.viewerOwnsNeeder || r.viewerIsNeeder;
  const youOwnHelp = r.viewerOwnsHelper || r.viewerIsHelper;
  if (youOwnNeed && youOwnHelp) {
    return { you: "Both people are in your address book, so you see everything and nothing leaves your workspace until you share it.", them: "Neither person is notified. Introducing them is entirely up to you." };
  }
  if (youOwnNeed) {
    const helperLine = helper.hidden ? `${possessive(helper.ownerName)} contact stays hidden: you see the offer, not the person.` : `You see ${helper.name} because they are in your address book or on Hub.`;
    return { you: `You see the need (${card.need.title}) because the person who has it is yours. ${helperLine}`, them: `${helper.ownerName} sees that someone in your network needs ${card.need.title.toLowerCase()}, with the name hidden, and can offer to help.` };
  }
  if (youOwnHelp) {
    const neederLine = needer.hidden ? `${possessive(needer.ownerName)} contact stays hidden: you see the need, not who has it.` : `You see ${needer.name} because they are in your address book or on Hub.`;
    return { you: `You see this because ${helper.name === "Hidden" ? "your contact" : helper.name} offers ${card.service.title.toLowerCase()}. ${neederLine}`, them: `${needer.ownerName} sees that someone in your network can help with ${card.need.title.toLowerCase()}, with the name hidden until you accept or share.` };
  }
  return { you: "You can introduce these two people: both sides reach you through your connections.", them: "Each side sees only their own person and the title of the other side's need or offer." };
}

/** "Why am I seeing this?" as a small modal: perspective, what is shared when, and what credits do. */
export function PerspectiveHelp({ card, compact }: { card: MatchCard; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const lines = perspectiveLines(card);
  const costly = card.actions.filter((a) => a.creditCost > 0);
  const earning = card.actions.filter((a) => a.creditEarned > 0);
  return (
    <>
      <Button size="sm" variant="ghost" icon={<HelpCircle size={13} />} onClick={(e) => { e.stopPropagation(); setOpen(true); }} title="Why am I seeing this?" data-testid="perspective-help">
        {compact ? null : "Why am I seeing this?"}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Why am I seeing this?">
        <div className="u-col" style={{ gap: 12 }} data-testid="perspective-help-body">
          <div>
            <div className={s.blockLabel}>What you see</div>
            <div className={s.small}>{lines.you}</div>
          </div>
          <div>
            <div className={s.blockLabel}>What the other side sees</div>
            <div className={s.small}>{lines.them}</div>
          </div>
          <div>
            <div className={s.blockLabel}>When details are shared</div>
            <div className={s.small}>Nothing is shared until someone accepts. Hidden people stay hidden; names, emails and phones are revealed only to the person who accepted the introduction, and only for the two people involved. Hub never messages anyone for you.</div>
          </div>
          {CREDITS_VISIBLE && <div>
            <div className={s.blockLabel}>What credits do</div>
            <div className={s.small}>
              Credits keep introductions deliberate. Accepting an introduction or revealing a hidden person uses one credit; sharing a contact that the other side accepts earns credits back. Plans with unlimited credits are never charged.
              {costly.length > 0 && ` Here: ${costly.map((a) => `${a.label} (${a.creditLabel})`).join(", ")}.`}
              {earning.length > 0 && ` You earn credits with: ${earning.map((a) => `${a.label} (${a.creditLabel})`).join(", ")}.`}
            </div>
          </div>}
          <div className="u-row" style={{ justifyContent: "flex-end" }}>
            <Button size="sm" onClick={() => setOpen(false)}>Got it</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
